<template>
  <component
    :is="statusComponent"
    :has-any-up-to-date-review="hasAnyUpToDateReview"
    :has-up-to-date-review-by-user="hasUpToDateReviewByUser"
    :preparation="preparation"
    :preparation-loading="preparationLoading"
    :review-loading="reviewLoading"
    :reviewed="reviewed"
    :reviews-to-show="reviewsToShow"
    :tracking-prefix="trackingPrefix"
    @mark-as-reviewed="toggleReviewed(true)"
    @unmark="toggleReviewed(false)"
    @toggle-reviewed="toggleReviewed"
    @toggle-prepared="togglePrepared"
  />
</template>

<script>
import { ref } from 'vue'
import { mapActions } from 'pinia'
import { sortBy } from 'lodash'

import SFetch from '@app/utils/sfetch'
import { SfLog } from '@app/adapters/logger'
import WorkflowReviewStatusBlock from '@app/application/workflows/screens/workflow-review/components/workflow-review-status-block.vue'
import WorkflowItemStatus from '@app/application/workflows/screens/workflow-review/components/workflow-item-status/item-status.vue'
import { useWorkflowReviewItemStatus } from '../store/pinia/workflow-review-item-status'

const WorkflowReviewStatus = {
  props: [
    'parentId',
    'review',
    'reviews',
    'preparation',
    'ledgerId',
    'itemClass',
    'itemId',
    'trackingPrefix',
    'shouldRenderInline',
    'newInlineComponent',
  ],
  emits: ['item-review-update', 'item-preparation-update'],
  components: {
    WorkflowReviewStatusBlock,
    WorkflowItemStatus,
  },
  computed: {
    statusComponent() {
      return this.shouldRenderInline
        ? this.statusInlineComponent
        : WorkflowReviewStatusBlock
    },

    reviewed() {
      return this.reviews && this.reviews.length > 0
    },
    reviewedByUser() {
      return !!this.review
    },
    hasAnyUpToDateReview() {
      return (
        this.reviewed &&
        this.reviews.some((review) => review.outdatedAt === null)
      )
    },
    hasUpToDateReviewByUser() {
      return this.reviewedByUser && this.review.outdatedAt === null
    },
    reviewsToShow() {
      const filteredReviews = this.hasAnyUpToDateReview
        ? this.reviews.filter((review) => review.outdatedAt === null)
        : this.reviews

      const currentUserId = parseInt(Silverfin.currentUserId())

      return sortBy(filteredReviews, [
        // 1) current user first
        (r) =>
          r.user_id === currentUserId || r.userId === currentUserId ? 0 : 1,
        // 2) then by time
        (r) => new Date(r.time).getTime(),
      ])
    },
  },
  setup() {
    const preparationLoading = ref(false)
    const reviewLoading = ref(false)

    return {
      preparationLoading,
      reviewLoading,
      statusInlineComponent: WorkflowItemStatus,
    }
  },
  methods: {
    togglePrepared(prepared) {
      if (prepared) {
        this.markAsPrepared()
      } else {
        this.unmarkAsPrepared()
      }
    },
    toggleReviewed(reviewed) {
      if (reviewed) {
        this.markAsReviewed()
      } else {
        this.unmarkAsReviewed()
      }
    },
    ...mapActions(useWorkflowReviewItemStatus, ['updateReviewItemStatus']),
    emitItemReviewUpdate(review, reviews) {
      const data = {
        itemId: this.itemId,
        itemClass: this.itemClass,
        parentId: this.parentId,
        review,
        reviews,
      }
      this.updateReviewItemStatus(data)
      this.$emit('item-review-update', data)
    },
    async markAsReviewed() {
      const url = Silverfin.Routes.ledger_reconciliation_reviews_path(
        Silverfin.currentFirmId,
        Silverfin.currentCompanyId,
        this.ledgerId,
        this.itemClass,
        this.itemId
      )

      this.reviewLoading = true
      try {
        const data = await SFetch.jsonWithAlert(url, { method: 'POST' })
        const { reviews, review } = data
        this.emitItemReviewUpdate(review, reviews)
      } finally {
        this.reviewLoading = false
      }
    },
    async unmarkAsReviewed() {
      const url = Silverfin.Routes.ledger_reconciliation_review_path(
        Silverfin.currentFirmId,
        Silverfin.currentCompanyId,
        this.ledgerId,
        this.itemClass,
        this.itemId,
        this.review.id
      )

      this.reviewLoading = true
      try {
        const data = await SFetch.jsonWithAlert(url, { method: 'DELETE' })
        const { reviews, review } = data
        this.emitItemReviewUpdate(review, reviews)
      } finally {
        this.reviewLoading = false
      }
    },
    async markAsPrepared() {
      const url = Silverfin.Routes.ledger_reconciliation_preparations_path(
        Silverfin.currentFirmId,
        Silverfin.currentCompanyId,
        this.ledgerId,
        this.itemClass,
        this.itemId
      )

      this.preparationLoading = true
      try {
        const preparation = await SFetch.json(url, { method: 'POST' })

        this.$emit('item-preparation-update', {
          itemId: this.itemId,
          itemClass: this.itemClass,
          parentId: this.parentId,
          preparation,
        })
      } catch (e) {
        SfLog.error('Cannot mark as prepared', {
          extra: {
            error: e,
          },
        })
      } finally {
        this.preparationLoading = false
      }
    },

    async unmarkAsPrepared() {
      const url = Silverfin.Routes.ledger_reconciliation_preparation_path(
        Silverfin.currentFirmId,
        Silverfin.currentCompanyId,
        this.ledgerId,
        this.itemClass,
        this.itemId,
        this.preparation.id
      )

      this.preparationLoading = true
      try {
        await SFetch.json(url, { method: 'DELETE' })

        this.$emit('item-preparation-update', {
          itemId: this.itemId,
          itemClass: this.itemClass,
          parentId: this.parentId,
          preparation: null,
        })
      } catch (e) {
        SfLog.error('Cannot unmark as prepared', {
          extra: {
            error: e,
          },
        })
      } finally {
        this.preparationLoading = false
      }
    },
  },
}

export default WorkflowReviewStatus
</script>
